import useParityConfirm from '../common/useParityConfirm';
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import AccessDeniedResponse from "../common/AccessDeniedResponse";
import MainTitle from "../common/MainTitle";
import TableMainSearch from "../common/MainSearch";
import RowPhoto from "../common/TdImg";
import Table from "../common/Table";
import '../common/parity.css';

import lbl from "../../data/Labels.json";
import db from "../../services/query";
import {
  ATTENDANCE_ABSENT,
  ATTENDANCE_PRESENT,
  attendanceWritesEnabled,
  availableStudentIds,
  buildAttendanceWritePayload,
} from "./attendanceWrite";

const writeErrorMessage = (err) => {
  const status = err?.response?.status || err?.response?.data?.error?.statusCode;
  const serverMessage = err?.response?.data?.error?.message;

  if (status === 400) return serverMessage || "درخواست حاضری معتبر نیست.";
  if (status === 401) return "برای ثبت حاضری دوباره وارد سیستم شوید.";
  if (status === 403) return "شما اجازه ثبت حاضری این درس را ندارید.";
  if (status >= 500) return "ثبت حاضری در سرور ناموفق بود. دوباره کوشش کنید.";
  return serverMessage || "ثبت حاضری ناموفق بود. دوباره کوشش کنید.";
};

const StudentRoster = ({ match, location }) => {
  const [confirm, confirmationDialog] = useParityConfirm();
  const { lectureId, date } = match.params;
  const navigationContext = location?.state || {};
  const [rows, setRows] = useState([]);
  const context = {
    subjectName: rows[0]?.subject_name,
    classDesc: rows[0]?.class_desc,
    ...navigationContext,
  };
  const [reload, setReload] = useState(0);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [writeFeedback, setWriteFeedback] = useState("");
  const submittingRef = useRef(false);
  const writesEnabled = attendanceWritesEnabled(
    process.env.REACT_APP_ENABLE_ATTENDANCE_WRITES
  );

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setDenied(false);
      setErrorMsg("");
      try {
        const { data: body } = await db.getAll(
          `timetables/students?date=${encodeURIComponent(date)}&lectureId=${encodeURIComponent(lectureId)}`
        );
        if (!cancelled) {
          setRows(Array.isArray(body?.data) ? body.data : []);
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const body = err?.response?.data;
        setRows([]);
        if (status === 403 || body?.error?.statusCode === 403) {
          setDenied(true);
        } else {
          setErrorMsg(body?.error?.message || "بارگیری لیست حاضری ناموفق بود.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [date, lectureId, reload]);

  const attendanceState = (state) => {
    if (Number(state) === 1) return lbl.Present;
    if (Number(state) === 2) return lbl.Absent;
    if (Number(state) === 3) return lbl.Substitute;
    if (Number(state) === 4) return lbl.Holiday;
    if (state == null || state === "") return "ثبت نشده";
    return `حالت ${state}`;
  };

  const rosterStudentIds = availableStudentIds(rows);
  const presentCount = rows.filter((row) => Number(row.attendance_state) === 1).length;
  const absentCount = rows.filter((row) => Number(row.attendance_state) === 2).length;
  const totalCount = rows.length || 1;
  const presentPct = Math.round((presentCount / totalCount) * 100);
  const allSelected =
    rosterStudentIds.length > 0 &&
    rosterStudentIds.every((studentId) => selectedIds.has(studentId));

  const toggleStudent = (studentId) => {
    const normalizedId = Number(studentId);
    if (submittingRef.current || !rosterStudentIds.includes(normalizedId)) return;

    setWriteFeedback("");
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(normalizedId)) next.delete(normalizedId);
      else next.add(normalizedId);
      return next;
    });
  };

  const selectAll = () => {
    if (!submittingRef.current) {
      setWriteFeedback("");
      setSelectedIds(new Set(rosterStudentIds));
    }
  };

  const clearSelection = () => {
    if (!submittingRef.current) {
      setWriteFeedback("");
      setSelectedIds(new Set());
    }
  };

  const updateAttendance = async (state, studentIds) => {
    if (submittingRef.current) return;

    let payload;
    try {
      payload = buildAttendanceWritePayload({
        date,
        lectureId,
        state,
        selectedIds: studentIds ? new Set(studentIds) : selectedIds,
        rows,
      });
    } catch (err) {
      return;
    }

    const action = state === ATTENDANCE_PRESENT ? "حاضر" : "غیر حاضر";
    const confirmed = await confirm(
      `ثبت ${action}\nدرس: ${lectureId}\nتاریخ: ${date}\nتعداد محصلان انتخاب شده: ${payload.studentIds.length}`
    );
    if (!confirmed || submittingRef.current) return;

    submittingRef.current = true;
    setSubmitting(true);
    setWriteFeedback("");

    try {
      const { data: body } = await db.put("attendance/students", payload);
      if (!Array.isArray(body?.data)) {
        throw new Error("Attendance response did not contain a roster");
      }
      setRows(body.data);
      setSelectedIds(new Set());
      setWriteFeedback(`حاضری ${payload.studentIds.length} محصل با موفقیت ثبت شد.`);
      try {
        const { data: fresh } = await db.getAll(
          `timetables/students?date=${encodeURIComponent(date)}&lectureId=${encodeURIComponent(lectureId)}`
        );
        if (Array.isArray(fresh?.data)) setRows(fresh.data);
      } catch (_) {
        /* Keep the writer roster if reload fails. */
      }
    } catch (err) {
      setWriteFeedback(writeErrorMessage(err));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const columns = [
    ...(writesEnabled
      ? [
          {
            key: "attendance_selection",
            label: "انتخاب",
            content: (row) => {
              const studentId = Number(row.student_id);
              const available = rosterStudentIds.includes(studentId);
              return (
                <input
                  type="checkbox"
                  aria-label={`Select student ${studentId}`}
                  checked={available && selectedIds.has(studentId)}
                  disabled={!available || submitting}
                  onChange={() => toggleStudent(studentId)}
                />
              );
            },
          },
        ]
      : []),
    { key: "serial", label: "شماره", content: (row) => rows.indexOf(row) + 1 },
    {
      path: "student_photo",
      label: "عکس",
      content: (row) => <RowPhoto src={row.student_photo} />,
    },
    { path: "student_name", label: "اسم" },
    { path: "student_father_name", label: "ولد" },
    {
      path: "attendance_state",
      label: "حالت حاضری",
      content: (row) => (
        <span data-testid={`attendance-state-${row.student_id}`}>
          {attendanceState(row.attendance_state)}
        </span>
      ),
    },
    ...(writesEnabled
      ? [
          {
            key: "actions",
            label: "عملیات",
            content: (row) => {
              const studentId = Number(row.student_id);
              const present = Number(row.attendance_state) === 1;
              const disabled = submitting || !rosterStudentIds.includes(studentId);
              return (
                <div className={`parity-actions roster-toggle${present ? " is-present" : " is-absent"}`}>
                  <button
                    type="button"
                    className="roster-toggle-absent"
                    disabled={disabled}
                    aria-label={`Mark absent ${studentId}`}
                    onClick={() => updateAttendance(ATTENDANCE_ABSENT, [studentId])}
                  >
                    غیر حاضر
                  </button>
                  <button
                    type="button"
                    className="roster-toggle-present"
                    disabled={disabled}
                    aria-label={`Mark present ${studentId}`}
                    onClick={() => updateAttendance(ATTENDANCE_PRESENT, [studentId])}
                  >
                    حاضر
                  </button>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div className="main-content mis-surface roster-parity">
      {confirmationDialog}
      <div className="main-content-header">
        <div className="right-content">
          <MainTitle title={context.classDesc || "لیست حاضری محصلان"} />
        </div>
        <div className="center-content">
          {!denied ? <TableMainSearch setQuery={setQuery} /> : null}
        </div>
        <div className="left-content">
          <button
            className="mis-button is-secondary"
            disabled={loading || submitting}
            onClick={() => {
              setSelectedIds(new Set());
              setReload((v) => v + 1);
            }}
          >
            بارگیری مجدد
          </button>
          <Link className="mis-button is-secondary" to="/mis/timetable">
            بازگشت
          </Link>
        </div>
      </div>
      <div className="main-content-body pagination roster-body">
        {denied ? (
          <AccessDeniedResponse />
        ) : loading ? (
          <p>{lbl.Loading}</p>
        ) : errorMsg ? (
          <p role="alert">{errorMsg}</p>
        ) : (
          <div className="roster-layout">
            <div className="roster-main">
              {writesEnabled && rows.length > 0 ? (
                <div
                  className="roster-bulk-bar"
                  data-testid="attendance-write-controls"
                >
                  <button
                    type="button"
                    data-testid="select-all"
                    onClick={selectAll}
                    disabled={submitting || allSelected}
                  >
                    انتخاب همه
                  </button>
                  <button
                    type="button"
                    data-testid="clear-selection"
                    onClick={clearSelection}
                    disabled={submitting || selectedIds.size === 0}
                  >
                    پاک کردن انتخاب
                  </button>
                  <span>انتخاب شده: {selectedIds.size}</span>
                  <button
                    type="button"
                    data-testid="mark-present"
                    className="is-present"
                    onClick={() => updateAttendance(ATTENDANCE_PRESENT)}
                    disabled={submitting || selectedIds.size === 0}
                  >
                    {submitting ? "در حال ثبت..." : "حاضر"}
                  </button>
                  <button
                    type="button"
                    data-testid="mark-absent"
                    className="is-absent"
                    onClick={() => updateAttendance(ATTENDANCE_ABSENT)}
                    disabled={submitting || selectedIds.size === 0}
                  >
                    {submitting ? "در حال ثبت..." : "غیر حاضر"}
                  </button>
                  {writeFeedback ? (
                    <p role={writeFeedback.includes("موفقیت") ? "status" : "alert"}>
                      {writeFeedback}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {rows.length === 0 ? (
                <p>برای این درس و تاریخ محصلی ثبت نشده است.</p>
              ) : (
                <Table data={{ data: rows }} columns={columns} query={query} />
              )}
            </div>
            <aside className="roster-sidebar" aria-label="جزئیات درس">
              <div className="roster-sidebar-head">
                <strong>{context.subjectName || "مضمون"}</strong>
                <span>{date}</span>
                {context.time ? <small dir="ltr">{context.time}</small> : null}
                {context.classDesc ? <small>{context.classDesc}</small> : null}
              </div>
              <div className="roster-donut-wrap">
                <div
                  className="roster-donut"
                  style={{ "--present": `${presentPct}%` }}
                  aria-hidden="true"
                >
                  <span>{presentPct}%</span>
                </div>
                <div className="roster-summary">
                  <span>حاضر: {presentCount}</span>
                  <span>غیر حاضر: {absentCount}</span>
                  <span>مجموع: {rows.length}</span>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>
      <div className="main-content-footer">
        <div className="route-history-area" />
        <div className="pagination-area" />
      </div>
    </div>
  );
};

export default StudentRoster;
